/**
 * Token & storage management for the auth module.
 *
 * Native TypeScript implementations — no dependency on legacy @/utils/helper.
 * Uses @/modules/shared/utils for localStorage primitives.
 */
import axios from 'axios';
import {
  getLocalStorageItem,
  getStoredToken,
  setLocalStorageItem,
  removeLocalStorageItem,
} from '@/modules/shared/utils';

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

export const getAccessToken = (): string | null => getStoredToken('accessToken');

export const setAccessToken = (token: string): void => setLocalStorageItem('accessToken', token);

export const getRefreshToken = (): string | null => getStoredToken('refreshToken');

export const setRefreshToken = (token: string): void => setLocalStorageItem('refreshToken', token);

// ---------------------------------------------------------------------------
// Local data helpers
// ---------------------------------------------------------------------------

export const setLocalData = (key: string, value: unknown): void => {
  if (typeof window === 'undefined') return;
  const raw = typeof value === 'object' ? JSON.stringify(value) : String(value);
  localStorage.setItem(key, raw);
};

/** Remove one key. `clearLocalData` wipes the known auth keys; this removes a single named one. */
export const deleteLocalData = (key: string): void => {
  if (typeof window === 'undefined') return;
  removeLocalStorageItem(key);
};

export const getLocalData = (key: string): unknown => {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

// ---------------------------------------------------------------------------
// User data helpers
// ---------------------------------------------------------------------------

export const getUserData = (): Record<string, unknown> | null => {
  const raw = getLocalStorageItem('userData');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const setUserData = (user: object): void => {
  setLocalStorageItem('userData', JSON.stringify(user));
};

// ---------------------------------------------------------------------------
// Clear all local data
// ---------------------------------------------------------------------------

const LOCAL_KEYS = ['accessToken', 'refreshToken', 'currentAffiliate', 'userData', 'language'] as const;

export const clearLocalData = (): void => {
  LOCAL_KEYS.forEach((key) => removeLocalStorageItem(key));
};

// ---------------------------------------------------------------------------
// Axios header helpers
// ---------------------------------------------------------------------------

export const setAxiosHeaders = (token: string | null = getAccessToken()): void => {
  axios.defaults.headers.common['Content-Type'] = 'application/json';
  axios.defaults.withCredentials = true;
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};

/**
 * Accepts `string | number` because the id often arrives from `getLocalData('currentAffiliate')`,
 * which cannot promise a number. Coerced on the way in — axios stringifies header values regardless,
 * so this only makes the type honest.
 */
export const setAffiliateIdHeader = (id: string | number | null | undefined): void => {
  if (id) {
    axios.defaults.headers.common['VOU-AFF-ID'] = String(id);
  }
};

export const removeAxiosHeaders = (): void => {
  delete axios.defaults.headers.common['Authorization'];
  delete axios.defaults.headers.common['VOU-AFF-ID'];
};
